import { useMemo } from "react";
import { TrendingDown, Plus, FileText } from "lucide-react";
import { formatCurrency } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";
import { ExpenseItem } from "./types";

interface ExpenseListProps {
  expenses: ExpenseItem[] | undefined;
  onAddExpense?: () => void;
}

/** The event's own spending (vendor payments are counted under vendor cost), grouped by category. */
export default function ExpenseList({ expenses, onAddExpense }: ExpenseListProps) {
  const groups = useMemo(() => {
    const map = new Map<string, { name: string; total: number; items: ExpenseItem[] }>();
    for (const e of expenses || []) {
      const name = e.category || "Uncategorised";
      const group = map.get(name) || { name, total: 0, items: [] };
      group.total += Number(e.amount) || 0;
      group.items.push(e);
      map.set(name, group);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [expenses]);

  const total = groups.reduce((s, g) => s + g.total, 0);
  const count = expenses?.length || 0;

  const addButton = onAddExpense && (
    <button
      onClick={onAddExpense}
      className="h-10 sm:h-8 px-3 rounded-lg bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors flex items-center gap-1"
    >
      <Plus className="w-3.5 h-3.5" />
      Add expense
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
      <div className="px-4 py-3 bg-gradient-to-r from-rose-50 to-rose-100/50 border-b border-rose-100 flex items-center justify-between gap-3">
        <h3 className="font-bold text-stone-800 text-sm flex items-center gap-2">
          <TrendingDown className="w-4 h-4 text-rose-600" />
          Event expenses
          {count > 0 && <span className="text-xs bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full font-medium">{count}</span>}
        </h3>
        {addButton}
      </div>

      {count === 0 ? (
        <div className="p-6 text-center">
          <TrendingDown className="w-10 h-10 text-stone-200 mx-auto mb-2" />
          <p className="text-sm text-stone-500">No expenses recorded for this event</p>
          <p className="text-xs text-stone-400 mt-1">Diesel, extra staff, transport… anything added here counts in this event's profit.</p>
        </div>
      ) : (
        <>
          <div className="px-4 py-2 bg-rose-50 border-b border-rose-100 flex items-center justify-between">
            <span className="text-xs text-rose-700 font-medium">Total</span>
            <span className="text-sm font-bold text-rose-700 tabular-nums">{formatCurrency(total)}</span>
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-stone-100">
            {groups.map((g) => (
              <div key={g.name}>
                <div className="px-4 pt-3 pb-1 flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-stone-500">
                  <span>{g.name}</span>
                  <span className="tabular-nums">{formatCurrency(g.total)}</span>
                </div>
                {g.items.map((e, idx) => (
                  <div key={e.id ?? `${g.name}-${idx}`} className="px-4 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-800 truncate">{e.title}</p>
                      <p className="text-xs text-stone-500 truncate">
                        {e.date}
                        {e.paid_to ? ` · ${e.paid_to}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {e.proof_path && (
                        <a href={getImageUrl(e.proof_path)} target="_blank" rel="noopener noreferrer" aria-label="View receipt" className="p-1.5 text-emerald-600 hover:text-emerald-700">
                          <FileText className="w-4 h-4" />
                        </a>
                      )}
                      <span className="text-sm font-semibold text-rose-600 tabular-nums">{formatCurrency(Number(e.amount) || 0)}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
